import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@platform/core/lib/cx';
import { statusColor } from '@platform/core/lib/statusColors';
import { DataModelAttributes } from './DataModelAttributes';
import { DataModelDiagram } from './DataModelDiagram';
import { DataModelEntities } from './DataModelEntities';
import type { DataModelTab } from './DataModelTab';
import type { IDataModelBodyProps } from './IDataModelBodyProps';
import { useModelEntities } from './useModelEntities';
import { useModelText } from './useModelText';
import { useRelatedPills } from './useRelatedPills';

/**
 * «Модель даних» модуля (прототип v4 mdIsM): вкладки «Сутності» і «Діаграми»; сутності — перелік типів записів і атрибути вибраного (зі
 * статусами основного), діаграма — сутності з кратністю зв'язків і пігулками пов'язаних модулів, легенда видів. Клік по сутності на діаграмі
 * вибирає її й відкриває «Сутності».
 */
export function DataModelBody({ ws, module }: IDataModelBodyProps) {
  const { t } = useTranslation();
  const text = useModelText(ws, module.module.key);
  const entities = useModelEntities(ws, module);
  const related = useRelatedPills(ws, entities[0]?.rt);
  const [tab, setTab] = useState<DataModelTab>('entities');
  const [selected, setSelected] = useState(0);
  const entity = entities[Math.min(selected, entities.length - 1)];
  if (!entity) return null;
  const attrs = entities.reduce((n, e) => n + e.attrs.length, 0);
  const pick = (i: number) => { setSelected(i); setTab('entities'); };
  return (
    <div className="dm">
      <div className="dm-tabs" role="tablist">
        {(['entities', 'diagram'] as const).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={cx('dm-tab', tab === k && 'is-on')} onClick={() => setTab(k)}>{t(`modDesign.model.tabs.${k}`)}</button>
        ))}
      </div>
      {tab === 'entities' ? (
        <div className="dm-grid">
          <DataModelEntities entities={entities} selected={selected} onSelect={setSelected} />
          <DataModelAttributes entity={entity} statusLabel={(v) => (entity.rt ? text.status(entity.rt, v) : v)} statusColor={(v) => (entity.rt ? statusColor(entity.rt.statuses, v, entity.rt.key) : '')} />
        </div>
      ) : (
        <>
          <div className="dm-label"><span className="dm-label__title">{t('modDesign.model.diagram')}</span><span className="dm-label__sub">{t('modDesign.model.diagramSub', { types: t('modDesign.model.types', { count: entities.length }), attrs: t('modDesign.model.attrs', { count: attrs }) })}</span></div>
          <div className="dm-canvas">
            <DataModelDiagram entities={entities} selected={selected} onSelect={pick} related={related} moduleName={entities[0]!.name} />
          </div>
          <div className="dm-legend">
            <span><i className="dm-legend__box is-main" />{t('modDesign.model.legend.main')}</span>
            <span><i className="dm-legend__box is-child" />{t('modDesign.model.legend.child')}</span>
            <span><i className="dm-legend__box is-catalogue" />{t('modDesign.model.legend.catalogue')}</span>
            <span>{t('modDesign.model.legend.required')}</span>
            {related.length > 0 && <span>{t('modDesign.model.legend.pills')}</span>}
          </div>
        </>
      )}
    </div>
  );
}
